import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Forgot-password request screen. Calls the real
 * `useAuth().requestPasswordReset()` (-> `supabase.auth.resetPasswordForEmail`)
 * and ALWAYS shows the same generic confirmation, regardless of whether the
 * email is actually registered — this is the anti-enumeration requirement,
 * not a missing feature. Never branches UI state on account existence.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */
export default function ForgotPassword() {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await requestPasswordReset(email);
    setSubmitting(false);
    // Always the same outcome, whatever the backend actually found.
    setSent(true);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--alex-font-family)', background: 'var(--alex-app-bg)' }}>
      <header
        style={{
          height: 'var(--alex-topbar-height)',
          background: 'var(--alex-sidebar-bg-deep)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 32px',
          flexShrink: 0,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 19, letterSpacing: 0.3 }}>
          <span style={{ color: '#fff' }}>ALEX</span> <span style={{ color: 'var(--alex-primary)' }}>ERP</span>
          <span style={{ color: 'var(--alex-sidebar-text-muted)', fontWeight: 500, fontSize: 12, marginLeft: 10 }}>EL Amos Web ERP</span>
        </div>
      </header>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div className="alex-card" style={{ width: 400, padding: 40 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', color: 'var(--alex-text-primary)' }}>Lupa Password</h1>
          <p style={{ fontSize: 'var(--alex-fs-body)', color: 'var(--alex-text-secondary)', margin: '0 0 24px' }}>
            Masukkan email akun Anda. Kami akan mengirim instruksi reset password jika akun tersebut terdaftar.
          </p>

          {!sent ? (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <label style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', fontWeight: 600 }}>
                Email
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  autoFocus
                  autoComplete="username"
                  style={inputStyle}
                />
              </label>
              <button type="submit" disabled={submitting} style={{ ...primaryButtonStyle, opacity: submitting ? 0.7 : 1, cursor: submitting ? 'default' : 'pointer' }}>
                {submitting ? 'Memproses...' : 'Kirim Instruksi Reset'}
              </button>
            </form>
          ) : (
            <div
              role="status"
              style={{
                background: 'var(--alex-primary-soft)',
                border: '1px solid var(--alex-primary-border)',
                borderRadius: 'var(--alex-control-radius)',
                padding: '14px 16px',
                fontSize: 'var(--alex-fs-body)',
                color: 'var(--alex-text-primary)',
              }}
            >
              Jika email tersebut terdaftar di ALEX ERP, instruksi reset password akan dikirim.
            </div>
          )}

          <div style={{ marginTop: 24, textAlign: 'center', fontSize: 'var(--alex-fs-caption)' }}>
            <Link to="/login" style={{ color: 'var(--alex-primary)', fontWeight: 600, textDecoration: 'none' }}>
              Kembali ke halaman masuk
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

const inputStyle = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  marginTop: 6,
  border: '1px solid var(--alex-border)',
  borderRadius: 'var(--alex-control-radius)',
  fontSize: 'var(--alex-fs-body)',
  color: 'var(--alex-text-primary)',
  boxSizing: 'border-box',
} as const;

const primaryButtonStyle = {
  padding: '11px 12px',
  border: 'none',
  borderRadius: 'var(--alex-control-radius)',
  background: 'var(--alex-primary)',
  color: '#fff',
  fontSize: 'var(--alex-fs-body)',
  fontWeight: 700,
  cursor: 'pointer',
  width: '100%',
} as const;
